import { Can } from '@/components/can/Can';
import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { Button } from '@/design-system/button';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { useDynamicLocale } from '@/hoslog-intl';
import { EditIcon, LockCloseIcon, LockOpenIcon, TrashIcon } from '@/icons';
import { commaSeparator } from '@/lib/commaSeparator';
import { formatDate } from '@/lib/dateFormatter';
import { cn } from '@/lib/utils';
import { additionalValue } from '@/react-query/context/ApiClientContextController';
import { PLAN_ORIGIN, PlanManagement } from '@/services/planManagement/planManagement.types';
import { UpdateClubPlanStatusPayload } from '@/services/premiumStores/premiumStores.types';
import type { ColumnDef } from '@tanstack/react-table';
import { ReactNode } from 'react';
import AdminPlanForm from '../../form/AdminPlanForm';
import ClubPlanForm from '../../form/ClubPlanForm';

type ModalSize = 'sm' | 'md' | 'lg';
type ModalVariant = 'default' | 'destructive' | 'warning';

type OpenConfirmationProps = (data: {
  title: string;
  description: string;
  cancelLabel: string;
  actionLabel: string;
  content?: React.ReactNode;
  size?: ModalSize;
  variant?: ModalVariant;
  onAction: () => Promise<void>;
  onCancel: () => void;
}) => void;

type OnOpenProps = <T extends string>(config: {
  title: T;
  modalTitle: string | null;
  okText: string | ReactNode;
  component: ReactNode;
  cancelText?: string | ReactNode;
  size?: ModalSize;
  onCancel?: () => void | Promise<void>;
  onAction?: () => Promise<void>;
}) => void;
export function getColumns(
  openConfirmation: OpenConfirmationProps,
  onOpen: OnOpenProps,
  mutateAsync: (payload: UpdateClubPlanStatusPayload) => Promise<unknown>,

  t: ReturnType<typeof useDynamicLocale>['t'],
  clubid: string,
  commonClubId: additionalValue,
): ColumnDef<PlanManagement>[] {
  return [
    {
      accessorKey: 'sn',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.sn')} />,
      cell: ({ row, table }) => (
        <div className="font-medium text-caption text-primary truncate max-w-[10rem]">
          {getSerialNumber(table, row.index)}
        </div>
      ),
      enableHiding: false,
      enableSorting: false,
    },
    {
      accessorKey: 'planTitleJp',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.planTitle')} />,
      cell: ({ row }) => {
        return (
          <div className="flex items-center text-xs font-medium space-x-3 max-w-[20rem] truncate">
            {row.getValue('planTitleJp')}
          </div>
        );
      },
      meta: {
        hideColumnLabelKey: 'common.page.tableColumn.planTitle',
      },
    },
    {
      accessorKey: 'planType',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.planType')} />,
      cell: ({ row }) => {
        const planType: string = row.getValue('planType');
        const planTypeColor = planType.toLowerCase() === 'premium' ? 'text-link' : 'text-success';

        return (
          <div className={cn('font-medium text-caption text-primary', planTypeColor)}>{row.getValue('planType')}</div>
        );
      },
      meta: {
        hideColumnLabelKey: 'common.page.tableColumn.planType',
      },
    },
    {
      accessorKey: 'planTime',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.planTime')} />,
      cell: ({ row }) => <div className="font-medium text-caption text-primary">{row.getValue('planTime')}</div>,
      meta: {
        hideColumnLabelKey: 'common.page.tableColumn.planTime',
      },
    },
    {
      accessorKey: 'noOfPeople',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.numberOfPeople')} />
      ),
      cell: ({ row }) => <div className="font-medium text-caption text-primary">{row.getValue('noOfPeople')}</div>,
      meta: {
        hideColumnLabelKey: 'common.page.tableColumn.numberOfPeople',
      },
    },
    {
      accessorKey: 'planPrice',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.planPrice')} />,
      cell: ({ row }) => (
        <div className="font-medium text-caption text-primary text-right">
          {commaSeparator(row.getValue('planPrice'))}円
        </div>
      ),
      meta: {
        hideColumnLabelKey: 'common.page.tableColumn.planPrice',
      },
    },
    {
      id: 'createdDate',
      accessorKey: 'createdDate',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.createdDate')} />
      ),
      cell: ({ row }) => {
        return (
          <div className="font-medium text-caption text-primary whitespace-nowrap">
            {formatDate(Number(row.getValue('createdDate')), 'YYYY/MM/DD HH:mm:ss')}
          </div>
        );
      },
      meta: {
        hideColumnLabelKey: 'common.page.tableColumn.createdDate',
      },
      enableColumnFilter: true,
    },
    {
      accessorKey: 'updatedDate',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.updatedDate')} />
      ),
      cell: ({ row }) => {
        return (
          <div className="font-medium text-caption text-primary whitespace-nowrap">
            {row.getValue('updatedDate') ? formatDate(Number(row.getValue('updatedDate')), 'YYYY/MM/DD HH:mm:ss') : '-'}
          </div>
        );
      },
      meta: {
        hideColumnLabelKey: 'common.page.tableColumn.updatedDate',
      },
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.status')} />,
      cell: ({ row }) => {
        return (
          <div className="font-medium text-caption">
            {row.getValue('status') === 'A' ? (
              <div className="text-success">{t('common.page.table.active')}</div>
            ) : row.getValue('status') === 'P' ? (
              <div className="text-warning">{t('common.page.table.pending')}</div>
            ) : (
              <div className="text-destructive-text">{t('common.page.table.blocked')}</div>
            )}
          </div>
        );
      },
      meta: {
        hideColumnLabelKey: 'common.page.tableColumn.status',
      },
      enableColumnFilter: false,
    },
    {
      id: 'actions',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.actions')} />,
      cell: ({ row }) => {
        const isStatusBlocked = row.getValue('status') === 'B';
        return row.original.status !== 'P' ? (
          <div className="flex items-center gap-4">
            {row.original.planOrigin === PLAN_ORIGIN.clubPlan ? (
              <Can rule={{ allOf: [PERMS.club.premium.plan.detail, PERMS.club.premium.plan.update] }}>
                <EditIcon
                  className="text-foreground-caption hover:text-foreground-detail cursor-pointer"
                  height={16}
                  width={16}
                  onClick={() => {
                    onOpen({
                      modalTitle: 'common.page.modal.manageOriginalPlans',
                      component: (
                        <ClubPlanForm
                          planId={row.original.planId}
                          clubId={row.original.clubId}
                          commonClubId={commonClubId?.commonClubId}
                        />
                      ),
                      okText: t('common.page.modal.submit'),
                      size: 'lg',
                      title: 'Manage Original Plans',
                      cancelText: isStatusBlocked ? '' : t('common.page.modal.turnOff'),
                      onCancel: async () => {
                        await mutateAsync({
                          clubId: clubid,
                          planId: row.original.planId,
                          status: 'B',
                          planOrigin: row.original.planOrigin,
                        });
                      },
                    });
                  }}
                />
              </Can>
            ) : (
              <Can rule={{ allOf: [PERMS.club.premium.admin.plan.details, PERMS.club.premium.updateAdminPlanDetails] }}>
                <EditIcon
                  className="text-foreground-caption hover:text-foreground-detail cursor-pointer"
                  height={16}
                  width={16}
                  onClick={() => {
                    onOpen({
                      modalTitle: 'common.page.modal.manageAdminPlans',
                      component: <AdminPlanForm planId={row.original.planId} clubId={clubid} />,
                      okText: t('common.page.modal.submit'),
                      size: 'lg',
                      title: 'Manage Admin Plans',
                      cancelText: isStatusBlocked ? '' : t('common.page.modal.turnOff'),
                      onCancel: async () => {
                        await mutateAsync({
                          clubId: clubid,
                          planId: row.original.planId,
                          status: 'B',
                          planOrigin: row.original.planOrigin,
                        });
                      },
                    });
                  }}
                />
              </Can>
            )}
            <Can rule={{ allOf: [PERMS.club.premium.plan.status] }}>
              {row.original.status === 'B' && row.original.planOrigin === PLAN_ORIGIN.adminPlan ? null : (
                <Button
                  variant={'ghost'}
                  className="p-0 m-0"
                  onClick={() => {
                    openConfirmation({
                      title: t('common.page.modal.confirmation.title'),
                      description:
                        row.getValue('status') === 'A'
                          ? t('common.page.modal.blockMessage')
                          : t('common.page.modal.unblockMessage'),
                      cancelLabel: t('common.page.modal.no'),
                      actionLabel: t('common.page.modal.confirm'),
                      onAction: async () => {
                        await mutateAsync({
                          clubId: clubid,
                          planId: row.original.planId,
                          status: row.getValue('status') === 'A' ? 'B' : 'A',
                          planOrigin: row.original.planOrigin,
                        });
                      },
                      onCancel: () => {},
                      size: 'lg',
                      variant: 'warning',
                    });
                  }}
                >
                  {row.getValue('status') === 'A' ? (
                    <LockCloseIcon className="text-destructive-text size-4" />
                  ) : (
                    <LockOpenIcon className="text-link size-4" />
                  )}
                </Button>
              )}
            </Can>
            <Can rule={{ allOf: [PERMS.club.premium.plan.status] }}>
              {row.original.planOrigin === PLAN_ORIGIN.clubPlan && (
                <TrashIcon
                  size={16}
                  onClick={() => {
                    openConfirmation({
                      title: t('common.page.modal.confirmation.title'),
                      description: t('common.page.modal.deleteItemMessage'),
                      cancelLabel: t('common.page.modal.no'),
                      actionLabel: t('common.page.modal.confirm'),
                      onAction: async () => {
                        await mutateAsync({
                          clubId: row.original.clubId,
                          planId: row.original.planId,
                          status: 'D',
                          planOrigin: row.original.planOrigin,
                        });
                      },
                      onCancel: () => {},
                      size: 'lg',
                      variant: 'destructive',
                    });
                  }}
                  className="cursor-pointer text-destructive-text hover:text-destructive-text"
                />
              )}
            </Can>
          </div>
        ) : null;
      },
      size: 40,
      enableHiding: false,
    },
  ];
}
